/**
 * Vorlage „Köcheln lassen“.
 *
 * Topf auf kleiner Flamme, die Oberfläche bewegt sich nur leicht, einzelne
 * Bläschen steigen auf, etwas Dampf entweicht. Der Deckel liegt auf Wunsch
 * schräg auf. Eine Uhr zeigt die Garzeit.
 */
import { STROKE } from '../palette.ts';
import { compact, rotated, shape } from '../scene.ts';
import { bubbles, steam, surfaceLine } from '../parts/effects.ts';
import { lid } from '../parts/kitchen.ts';
import { arcArrow, counter, BADGE_RIGHT, durationLabel, timerBadge } from '../parts/stage.ts';
import { GRATE_Y, HEAT_LEVELS, HEAT_POWER, heatFromText, stove, stoveKnob } from '../parts/stove.ts';
import type { HeatLevel } from '../parts/stove.ts';
import { contents, pot } from '../parts/vessels.ts';
import { minutesFromText } from '../inference.ts';
import { booleanValue, choice, defineTemplate, numberValue, textValue } from '../template.ts';

/** Topf. */
const POT = { cx: 100, width: 112, height: 48 } as const;
const RIM_Y = GRATE_Y - POT.height;
const LEVEL = 0.26;
const KNOB_X = 206;

/** Vorlage „Köcheln lassen“. */
export const stoveSimmerTemplate = defineTemplate({
  id: 'stove.simmer',
  group: 'stove',
  title: 'Köcheln lassen',
  description:
    'Der Topf steht auf kleiner Flamme. Die Oberfläche bewegt sich nur leicht und es steigen nur einzelne Bläschen auf.',
  durationSeconds: 4,
  keywords: [
    'köcheln',
    'simmern',
    'leise kochen',
    'sanft kochen',
    'schwacher hitze',
    'niedriger hitze',
    'garen lassen',
  ],
  stillTime: 0.3,
  parameters: [
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 1,
      max: 240,
      step: 1,
      unit: 'Min',
      defaultValue: 20,
    },
    { kind: 'boolean', name: 'lid', label: 'Deckel schräg auflegen', defaultValue: true },
    choice('heat', 'Hitze', HEAT_LEVELS, 'low'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      minutes: minutesFromText(text),
      lid: lower.includes('ohne deckel') || lower.includes('offen') ? false : undefined,
      ...heatFromText(text),
    };
  },
  scene(values, t) {
    const minutes = numberValue(values, 'minutes', 20);
    const withLid = booleanValue(values, 'lid', true);
    const power = HEAT_POWER[textValue(values, 'heat', 'low') as HeatLevel];
    const vessel = pot(POT.cx, RIM_Y, POT.width, POT.height);
    const surfaceY = RIM_Y + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const ry = vessel.ry * (rx / vessel.rx);
    const burner = stove(POT.cx, power, t);
    const sauce = contents(vessel, LEVEL, 'shade', [
      shape(surfaceLine(POT.cx - rx, surfaceY - ry * 0.2, rx * 2, 0.8, t, 2), {
        stroke: 'deep',
        strokeWidth: STROKE.fine,
      }),
      ...bubbles(POT.cx - rx, surfaceY - ry * 0.9, rx * 2, ry * 1.8, 0.2 + power * 0.25, t, 9),
    ]);
    // Der Deckel liegt schräg auf und lässt rechts einen Spalt für den Dampf.
    const lidNode = withLid
      ? rotated(-7, POT.cx - POT.width / 2, RIM_Y, lid(POT.cx - 10, RIM_Y - 2, POT.width + 4))
      : null;
    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      sauce,
      ...vessel.front,
      lidNode,
      ...burner.front,
      ...steam(withLid ? POT.cx + 44 : POT.cx, RIM_Y - (withLid ? 10 : 6), t, {
        count: withLid ? 2 : 3,
        spread: withLid ? 10 : 22,
        height: 26,
        strength: 0.7,
      }),
      stoveKnob(KNOB_X, power),
      timerBadge(BADGE_RIGHT, 26, durationLabel(minutes), t),
    ]);
  },
  hints() {
    return [arcArrow(POT.cx, RIM_Y + 3, 36, 8, 200, 340)];
  },
});
